import React from 'react';
import { cn } from '@/utils/cn';

export interface TutaraSelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: string;
  options?: { value: string; label: string }[];
  error?: string;
  hint?: string;
  placeholder?: string;
  onValueChange?: (value: string) => void;
  children?: React.ReactNode;
  /**
   * Suppress the "nothing chosen" row entirely.
   *
   * For a select whose value is always one of the options — a two-state role cell in a grid, say —
   * the placeholder is an option the user can never meaningfully pick, and it makes a required
   * field look optional.
   */
  hidePlaceholder?: boolean;
}

export const TutaraSelect = React.forwardRef<HTMLSelectElement, TutaraSelectProps>(
  ({ className, label, options, error, hint, placeholder, onValueChange, onChange, children,
     hidePlaceholder = false, ...props }, ref) => {
    // The empty-valued row means "nothing chosen". Callers that already supply one of their own —
    // "Select Department", "None (Top-level team)" — used to get ours stacked on top of it: two
    // blank-valued rows, the caller's wording buried under a generic "Select an option...". Stand
    // down when they have covered it themselves.
    const hasOwnEmptyOption = options?.some((option) => option.value === '') ?? false;
    const showPlaceholder = !hasOwnEmptyOption && !hidePlaceholder;

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      // Call onValueChange if provided (controlled component pattern)
      if (onValueChange) {
        onValueChange(e.target.value);
      }
      // Also call onChange for backwards compatibility
      if (onChange) {
        onChange(e);
      }
    };

    return (
      <div className="space-y-1">
        {label && (
          <label className="block text-sm font-medium text-tutara-charcoal dark:text-gray-200">
            {label}
          </label>
        )}
        <select
          ref={ref}
          className={cn(
            'w-full px-3 py-2 rounded-lg border-2 transition-all',
            'bg-white dark:bg-gray-800',
            'text-tutara-charcoal dark:text-gray-200',
            'border-gray-300 dark:border-gray-600',
            'focus:border-tutara-turquoise focus:outline-none',
            'disabled:bg-gray-100 dark:disabled:bg-gray-900',
            'disabled:text-tutara-gray disabled:cursor-not-allowed',
            error && 'border-red-500',
            className
          )}
          onChange={handleChange}
          {...props}
        >
          {/* Render placeholder option, unless the caller brought their own */}
          {showPlaceholder && (
            <option value="">{placeholder || 'Select an option...'}</option>
          )}

          {/* Render options from props if provided */}
          {options && options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}

          {/* Render children if provided (allows for custom option elements) */}
          {children}
        </select>
        {hint && !error && (
          <p className="text-sm text-tutara-gray dark:text-gray-400">{hint}</p>
        )}
        {error && (
          <p className="text-sm text-red-500">{error}</p>
        )}
      </div>
    );
  }
);

TutaraSelect.displayName = 'TutaraSelect';
